import { useEffect, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useNavigate } from 'react-router-dom';

import { datadogRum } from '@datadog/browser-rum';
import { Honeybadger } from '@honeybadger-io/react';
import { shutdown } from '@intercom/messenger-js-sdk';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { requestApi } from 'api/Api';

import { current_admin } from 'components/admin/lib/adminState';
import NotFound from 'components/practice/Dashboard/components/NotFound';
import { isAmbientListeningActive } from 'components/practice/charts/ClinicalNote/MyScribeAI/Templates/AmbientListening/state';
import {
  currentPractice,
  ownerPractices,
  ownerSelectedLocation
} from 'components/practice/practiceState';
import Modal from 'components/shared/Modal/Modal';
import { hasBeenAuthChecked, userState } from 'components/state';

const AutoLogout = () => {
  const [user, setUser] = useRecoilState(userState);
  const [adminState, setAdminState] = useRecoilState(current_admin);

  const setPractice = useSetRecoilState(currentPractice);
  const [remaining, setRemaining] = useState(5 * 60);
  const [showModal, setShowModal] = useState(false);
  const setHasBeenAuthChecked = useSetRecoilState(hasBeenAuthChecked);
  const setOwnerSelectedLocation = useSetRecoilState(ownerSelectedLocation);
  const setOwnerPractices = useSetRecoilState(ownerPractices);
  const isAmbientListeningInUse = useRecoilValue(isAmbientListeningActive);
  const navigate = useNavigate();

  const logout = async () => {
    try {
      const isAdmin = adminState?.logged_in;
      const logoutUrl = isAdmin ? '/api/admin/logout' : '/api/user/logout';

      try {
        await requestApi({ url: logoutUrl, navigate });
      } catch (e) {
        console.error(e);
      }

      sessionStorage.removeItem('myriad-session-reference-id');
      shutdown(); // intercom

      setUser({
        user_id: '',
        f_name: '',
        l_name: '',
        email: '',
        state: '',
        kind: '',
        logged_in: false
      });

      if (isAdmin) {
        setAdminState({
          user_id: '',
          f_name: '',
          l_name: '',
          email: '',
          state: '',
          role: '',
          logged_in: false
        });
      }

      datadogRum.clearUser();

      setOwnerPractices([]);
      setOwnerSelectedLocation(null);
      setHasBeenAuthChecked({ checked: false, isImpersonate: false });
      setPractice({ id: null, name: '', email: '', timezone: '', display_settings: null });

      const isKiosk = !!localStorage.getItem('myriadIsKiosk');
      window.localStorage.clear();
      localStorage.setItem('logout-event', `logout-${new Date().toISOString()}`);

      if (isKiosk) {
        localStorage.setItem('myriadIsKiosk', true);
        navigate('/kiosk/idle');
        return;
      }

      const path = isAdmin ? '/admin/login' : '/login';
      navigate(path, { state: { idle: true } });
    } catch (error) {
      window.localStorage.clear();
      Honeybadger.notify(
        `file: AutoLogout, method: logout - catch, error: ${error ?? 'Theres been an error'}`
      );
    }
  };

  const { reset } = useIdleTimer({
    timeout: 10 * 60 * 1000,
    onIdle: () => {
      setShowModal(true);
      setRemaining(5 * 60); // Start the countdown from 5 minutes when the modal is shown
    },
    onActive: () => {
      setShowModal(false);
      reset();
    },
    onAction: reset,
    debounce: 500,
    crossTab: { type: 'localStorage', emitOnAllTabs: true },
    disabled: !!isAmbientListeningInUse // ⛔ Prevent auto-logout trigger
  });

  useEffect(() => {
    let interval;
    if (showModal) {
      interval = setInterval(() => {
        setRemaining((prevRemaining) => {
          if (prevRemaining <= 1) {
            clearInterval(interval);
            datadogRum.addError(new Error(`Idle timeout logout triggered`), {
              context: {
                file: 'AutoLogout',
                method: 'idle-timeout',
                userId: user?.user_id || 'unknown',
                adminId: adminState?.user_id || 'unknown',
                action: 'logout_triggered'
              },
              tags: ['error', 'frontend', 'autologout', 'idle-timeout']
            });
            logout();
            return 0;
          }
          return prevRemaining - 1;
        });
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [showModal]);

  useEffect(() => {
    const handleStorageEvent = (event) => {
      if (event.key === 'logout-event' && (user?.logged_in || adminState?.logged_in)) {
        logout();
      }
    };

    window.addEventListener('storage', handleStorageEvent);

    return () => {
      window.removeEventListener('storage', handleStorageEvent);
    };
  }, [user.logged_in, adminState?.logged_in]);

  useEffect(() => {
    const checkSessionValid = async () => {
      try {
        const resData = await requestApi({
          url: '/api/user/check_token',
          navigate,
          throwError: true
        });
        if (
          resData &&
          resData.token_valid === false &&
          (user?.logged_in || adminState?.logged_in)
        ) {
          datadogRum.addError(
            new Error(`Session validation logout triggered due to invalid token`),
            {
              context: {
                file: 'AutoLogout',
                method: 'session-validation',
                userId: user?.user_id || 'unknown',
                adminId: adminState?.user_id || 'unknown',
                tokenValid: resData?.token_valid,
                action: 'logout_triggered'
              },
              tags: ['error', 'frontend', 'autologout', 'session-validation']
            }
          );
          logout();
        }
      } catch (error) {
        Honeybadger.notify(
          `file: AutoLogout, method: checkSessionValid - catch, error: ${error ?? 'Theres been an error'}`
        );
        console.error('Failed to check session heartbeat.', error);
      }
    };

    const interval = setInterval(
      () => {
        const currentPath = location?.pathname || '';

        if (
          (user?.logged_in || adminState?.logged_in) &&
          !currentPath.includes('/login') &&
          !currentPath.includes('/admin/login')
        ) {
          checkSessionValid();
        }
      },
      10 * 60 * 1000
    );
    return () => clearInterval(interval);
  }, [user?.logged_in, adminState?.logged_in]);

  useEffect(() => {
    const currentPath = location?.pathname || '';
    const isLoginPage = currentPath.includes('/login') || currentPath.includes('/admin/login');

    if (isLoginPage && showModal) {
      setShowModal(false);
    }
  }, [location?.pathname, showModal]);

  if (!user?.logged_in && !adminState?.logged_in) return null;

  return (
    <Modal
      handleOpen={showModal}
      disableHeader={true}
      shouldCloseOnOverlayClick={false}
      overlayClassName="z-[300]"
      isFooter={false}
      isCentered>
      <div className="my-10">
        <NotFound
          icon="illustration-automatization"
          title="Are you still there?"
          description={`You will be logged out in ${Math.floor(remaining / 60)}:${
            remaining % 60 < 10 ? `0${remaining % 60}` : remaining % 60
          } minutes due to inactivity.`}
          className="pt-0"
        />
      </div>
    </Modal>
  );
};
export default AutoLogout;
